import React, { useEffect } from 'react';
import styled from 'styled-components';
import { motion, useReducedMotion } from 'framer-motion';
import { FaChartLine, FaUsers, FaLightbulb, FaRocket } from 'react-icons/fa';
import Section from '../components/common/Section';
import usePageTransition from '../hooks/usePageTransition';

const PageHeader = styled.div`
  background: linear-gradient(135deg, ${({ theme }) => theme.colors.primary} 0%, ${({ theme }) => theme.colors.secondary} 100%);
  padding: ${({ theme }) => `${theme.space['3xl']} 0`};
  color: ${({ theme }) => theme.colors.white};
  text-align: center;
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-image: url('/assets/images/pattern.svg');
    opacity: 0.1;
    z-index: 1;
  }
  
  .container {
    position: relative;
    z-index: 2;
  }
`;

const PageTitle = styled(motion.h1)`
  margin-bottom: ${({ theme }) => theme.space.md};
  font-family: ${({ theme }) => theme.fonts.heading};
  font-weight: ${({ theme }) => theme.fontWeights.bold};
  font-size: 3.5rem;
  background: linear-gradient(135deg, #fff 0%, ${({ theme }) => theme.colors.accent} 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  display: inline-block;
`;

const PageDescription = styled(motion.p)`
  max-width: 700px;
  margin: 0 auto;
  font-size: ${({ theme }) => theme.fontSizes.xl};
  line-height: 1.6;
  font-family: ${({ theme }) => theme.fonts.body};
`;

const AboutContent = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${({ theme }) => theme.space.xl};
  align-items: center;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.lg}) {
    grid-template-columns: 1fr;
  }
`;

const AboutText = styled(motion.div)`
  @media (max-width: ${({ theme }) => theme.breakpoints.lg}) {
    order: 2;
  }
`;

const AboutImage = styled(motion.div)`
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  overflow: hidden;
  box-shadow: ${({ theme }) => theme.shadows.lg};
  height: 100%;
  min-height: 500px;
  background-image: url('https://images.unsplash.com/photo-1581091226825-a6a2a5aee158?q=80&w=2070&auto=format&fit=crop');
  background-size: cover;
  background-position: center;
  position: relative;
  
  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: linear-gradient(135deg, rgba(10, 26, 65, 0.6) 0%, rgba(22, 211, 169, 0.3) 100%);
  }
  
  &::before {
    content: '';
    position: absolute;
    top: 10px;
    left: 10px;
    right: 10px;
    bottom: 10px;
    border: 2px dashed ${({ theme }) => theme.colors.accent};
    border-radius: ${({ theme }) => theme.borderRadius.md};
    opacity: 0.7;
    z-index: 1;
  }
  
  @media (max-width: ${({ theme }) => theme.breakpoints.lg}) {
    order: 1;
  }
`;

const SectionTitle = styled(motion.h2)`
  margin-bottom: ${({ theme }) => theme.space.lg};
  font-family: ${({ theme }) => theme.fonts.heading};
  font-weight: ${({ theme }) => theme.fontWeights.bold};
  font-size: ${({ theme }) => theme.fontSizes['3xl']};
  color: ${({ theme }) => theme.colors.primary};
  position: relative;
  display: inline-block;
  
  &::after {
    content: '';
    position: absolute;
    bottom: -10px;
    left: 0;
    width: 80px;
    height: 3px;
    background-color: ${({ theme }) => theme.colors.accent};
  }
`;

const ValuePropositions = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: ${({ theme }) => theme.space.xl};
  margin-top: ${({ theme }) => theme.space.xl};
  
  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    grid-template-columns: 1fr;
  }
`;

const ValueCard = styled(motion.div)`
  background-color: ${({ theme }) => theme.colors.white};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  padding: ${({ theme }) => theme.space.xl};
  box-shadow: ${({ theme }) => theme.shadows.md};
  display: flex;
  flex-direction: column;
  height: 100%;
  position: relative;
  overflow: hidden;
  border-bottom: 3px solid transparent;
  transition: all 0.4s ease;
  
  &:hover {
    transform: translateY(-10px);
    border-bottom: 3px solid ${({ theme }) => theme.colors.accent};
    box-shadow: ${({ theme }) => theme.shadows.lg};
  }
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 5px;
    background: ${({ theme }) => theme.colors.gradient.accent};
    transform: scaleX(0);
    transform-origin: left;
    transition: transform 0.4s ease;
  }
  
  &:hover::before {
    transform: scaleX(1);
  }
`;

const ValueIcon = styled(motion.div)`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 70px;
  height: 70px;
  border-radius: ${({ theme }) => theme.borderRadius.blob};
  background: ${({ theme }) => theme.colors.gradient.accent};
  color: ${({ theme }) => theme.colors.dark};
  font-size: 1.75rem;
  margin-bottom: ${({ theme }) => theme.space.md};
  box-shadow: ${({ theme }) => theme.shadows.neon};
  position: relative;
  z-index: 1;
  
  &::before {
    content: '';
    position: absolute;
    top: -5px;
    left: -5px;
    right: -5px;
    bottom: -5px;
    border-radius: ${({ theme }) => theme.borderRadius.blob};
    background: ${({ theme }) => theme.colors.gradient.neon};
    z-index: -1;
    opacity: 0;
    transition: opacity 0.3s ease;
  }
  
  &:hover::before {
    opacity: 0.5;
  }
`;

const ValueTitle = styled.h3`
  margin-bottom: ${({ theme }) => theme.space.sm};
`;

const ValueDescription = styled.p`
  color: ${({ theme }) => theme.colors.grey[700]};
  margin-bottom: 0;
`;

const TeamSection = styled.div`
  margin-top: ${({ theme }) => theme.space['3xl']};
  text-align: center;
`;

const TeamGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: ${({ theme }) => theme.space.xl};
  margin-top: ${({ theme }) => theme.space.xl};
  
  @media (max-width: ${({ theme }) => theme.breakpoints.xl}) {
    grid-template-columns: repeat(3, 1fr);
  }
  
  @media (max-width: ${({ theme }) => theme.breakpoints.lg}) {
    grid-template-columns: repeat(2, 1fr);
  }
  
  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    grid-template-columns: 1fr;
  }
`;

const TeamMember = styled(motion.div)`
  text-align: center;
`;

const MemberImage = styled.div<{ imageSrc: string }>`
  width: 100%;
  height: 250px;
  border-radius: ${({ theme }) => theme.borderRadius.md};
  background-image: url(${({ imageSrc }) => imageSrc});
  background-size: cover;
  background-position: center;
  margin-bottom: ${({ theme }) => theme.space.md};
  box-shadow: ${({ theme }) => theme.shadows.md};
`;

const MemberName = styled.h3`
  margin-bottom: ${({ theme }) => theme.space.xs};
`;

const MemberPosition = styled.p`
  color: ${({ theme }) => theme.colors.primary};
  font-weight: ${({ theme }) => theme.fontWeights.medium};
  margin-bottom: ${({ theme }) => theme.space.sm};
`;

const MemberBio = styled.p`
  color: ${({ theme }) => theme.colors.grey[700]};
  font-size: ${({ theme }) => theme.fontSizes.sm};
`;

const values = [
  {
    icon: <FaChartLine />,
    title: 'Data-Driven Transformation',
    description: 'We leverage advanced analytics and AI to transform your business operations, ensuring measurable results and continuous improvement.'
  },
  {
    icon: <FaUsers />,
    title: 'Client-Centric Solutions',
    description: 'Your business goals are our priority. We create tailored digital solutions that address your unique challenges and objectives.'
  },
  {
    icon: <FaLightbulb />,
    title: 'Innovative Technology',
    description: 'We stay at the forefront of digital transformation and emerging technologies to provide cutting-edge solutions that keep you ahead of the competition.'
  },
  {
    icon: <FaRocket />,
    title: 'Strategic Growth',
    description: 'Our comprehensive digital strategies are designed to drive sustainable growth, enhance digital presence, and maximize ROI for your business.'
  }
];

// Team section removed as requested

const About: React.FC = () => {
  // Use our custom hook for optimized page transitions
  const { isReturnVisit, isPageReady } = usePageTransition();
  const prefersReducedMotion = useReducedMotion();
  
  // Optimize animations based on user preferences and return visits
  const animationSettings = {
    // If user prefers reduced motion or is a return visitor, use simpler animations
    initial: prefersReducedMotion || isReturnVisit ? { opacity: 0 } : { opacity: 0, y: -20 },
    animate: { opacity: 1, y: 0 },
    transition: { 
      type: 'spring', 
      stiffness: prefersReducedMotion || isReturnVisit ? 100 : 50, 
      damping: prefersReducedMotion || isReturnVisit ? 20 : 10,
      duration: prefersReducedMotion || isReturnVisit ? 0.2 : undefined
    }
  };
  
  // Preload the about image
  useEffect(() => {
    const img = new Image();
    img.src = 'https://images.unsplash.com/photo-1581091226825-a6a2a5aee158?q=80&w=2070&auto=format&fit=crop';
  }, []);
  // Don't render until the page is ready
  if (!isPageReady) {
    return <div style={{ height: '100vh' }} />; // Empty placeholder while loading
  }
  
  return (
    <>
      <PageHeader>
        <div className="container">
          <PageTitle
            initial={animationSettings.initial}
            animate={animationSettings.animate}
            transition={animationSettings.transition}
          >
            About Data Digital
          </PageTitle>
          <PageDescription
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ type: 'spring', stiffness: 50, damping: 10, delay: 0.2 }}
          >
            We're a team of digital transformation experts passionate about leveraging technology to drive business growth
          </PageDescription>
        </div>
      </PageHeader>
      
      <Section id="about">
        <AboutContent>
          <AboutText
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: '-100px' }}
            transition={{ type: 'spring', stiffness: 50, damping: 15 }}
          >
            <SectionTitle
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-100px' }}
              transition={{ type: 'spring', stiffness: 50, damping: 15, delay: 0.1 }}
            >
              Our Story
            </SectionTitle>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-100px' }}
              transition={{ type: 'spring', stiffness: 50, damping: 15, delay: 0.2 }}
              style={{ fontSize: '1.1rem', lineHeight: '1.8', marginBottom: '1.5rem' }}
            >
              Founded in 2018, Data Digital began with a clear vision: to transform how businesses approach digital transformation by putting innovation and data at the heart of every decision.
            </motion.p>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-100px' }}
              transition={{ type: 'spring', stiffness: 50, damping: 15, delay: 0.3 }}
              style={{ fontSize: '1.1rem', lineHeight: '1.8', marginBottom: '1.5rem' }}
            >
              Our leadership team recognized that most digital agencies were focusing on aesthetics rather than strategic outcomes. We assembled a team of experts in data science, software engineering, UX design, and business strategy to create a new kind of agency—one that combines creativity with technological precision.
            </motion.p>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-100px' }}
              transition={{ type: 'spring', stiffness: 50, damping: 15, delay: 0.4 }}
              style={{ fontSize: '1.1rem', lineHeight: '1.8', marginBottom: '2rem' }}
            >
              Today, we've helped over 300 businesses across various industries achieve digital transformation and measurable growth. We pride ourselves on staying at the forefront of emerging technologies and methodologies, ensuring our clients always have access to the most effective digital strategies and solutions.
            </motion.p>
            
            <ValuePropositions>
              {values.map((value, index) => (
                <ValueCard
                  key={index}
                  initial={{ opacity: 0, y: 30, scale: 0.95 }}
                  whileInView={{ opacity: 1, y: 0, scale: 1 }}
                  viewport={{ once: true, margin: '-100px' }}
                  transition={{ type: 'spring', stiffness: 50, damping: 15, delay: 0.2 * index }}
                  whileHover={{ y: -10, transition: { duration: 0.3 } }}
                >
                  <ValueIcon
                    initial={{ scale: 0.8, rotate: -10 }}
                    animate={{ scale: 1, rotate: 0 }}
                    transition={{ type: 'spring', stiffness: 100, delay: 0.1 * index }}
                    whileHover={{ 
                      rotate: 5, 
                      scale: 1.1,
                      transition: { duration: 0.3 }
                    }}
                  >
                    {value.icon}
                  </ValueIcon>
                  <ValueTitle>{value.title}</ValueTitle>
                  <ValueDescription>{value.description}</ValueDescription>
                </ValueCard>
              ))}
            </ValuePropositions>
          </AboutText>
          
          <AboutImage
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true, margin: '-100px' }}
            transition={{ type: 'spring', stiffness: 50, damping: 15, delay: 0.2 }}
          />
        </AboutContent>
      </Section>
    </>
  );
};

export default About;
